<template>
    <div
        class="event-property-filter-tag"
        :class="{
            'event-property-filter-tag__selected': isOpen,
            'event-property-filter-tag__disabled': disabled,
            'event-property-filter-tag__inapplicable': !applicable,
        }"
        @click="onClick"
        @keydown="onKeydown">
        <pendo-icon
            v-if="!!iconType"
            class="tag-label-icon"
            :type="iconType"
            size="12"
            aria-hidden="true" />
        <span
            ref="tags"
            class="tag-label-text">
            <template v-if="truncateTagLabel && tags.length">
                <span
                    v-for="tag in tags"
                    :key="tag"
                    class="tag-label">
                    {{ tag }}
                </span>
            </template>
            <template v-if="!truncateTagLabel">
                {{ tagLabel }}
            </template>
        </span>
        <div
            v-if="truncatedCount > 0"
            aria-hidden="true"
            class="tag-label-truncated-count">
            +{{ truncatedCount }}
        </div>
    </div>
</template>

<script>
import { PendoIcon } from '@pendo/components';

export default {
    name: 'EventPropertFilterTag',
    components: {
        PendoIcon,
    },
    props: {
        tagLabel: {
            type: String,
            default: '',
        },
        isOpen: {
            type: Boolean,
            default: false,
        },
        disabled: {
            type: Boolean,
            default: false,
        },
        applicable: {
            type: Boolean,
            default: false,
        },
        iconType: {
            type: String,
            default: '',
        },
        value: {
            type: Array,
            default: () => [],
        },
        truncateTagLabel: {
            type: Boolean,
            default: false,
        },
    },
    emits: ['click', 'keydown'],
    data () {
        return {
            truncatedCount: 0,
        };
    },
    computed: {
        tags () {
            if (this.value.length) {
                return this.value.map((tag, index) => {
                    if (index !== this.value.length - 1) {
                        return `${tag}, `;
                    }

                    return tag;
                });
            }

            return [];
        },
    },
    watch: {
        async value () {
            if (this.truncateTagLabel) {
                await this.$nextTick();
                this.intersectionObserver?.disconnect();
                this.initIntersectionObserver();
            }
        },
    },
    async mounted () {
        if (this.truncateTagLabel) {
            await this.$nextTick();
            this.initIntersectionObserver();
        }
    },
    methods: {
        initIntersectionObserver () {
            const root = this.$refs.tags;
            this.intersectionObserver = new IntersectionObserver(this.onIntersectionChange.bind(this), {
                root,
                // eslint-disable-next-line id-length
                threshold: Array.from({ length: 101 }, (_, i) => i / 100),
            });

            const items = this.$refs.tags?.querySelectorAll('.tag-label');
            items.forEach((item) => {
                this.intersectionObserver.observe(item);
            });
        },
        onIntersectionChange (entries) {
            const canvas = document.createElement('canvas');
            const context = canvas.getContext('2d');
            const ellipsisWidth = 11;

            entries.forEach((entry) => {
                const ratio = entry.intersectionRatio;
                const item = entry.target;
                const text = (item.textContent || '').trim();
                const textWidth = context.measureText(text).width;
                const firstLetterWidth = context.measureText(text.charAt(0)).width;
                const truncationWidth = Math.min(ellipsisWidth + firstLetterWidth, textWidth);

                const adjustedRatio = truncationWidth / item.offsetWidth;
                item.dataset.visibility = ratio < adjustedRatio ? 'hidden' : 'visible';
            });

            const hiddenItems = this.$refs.tags?.querySelectorAll('.tag-label[data-visibility="hidden"]').length;
            this.truncatedCount = hiddenItems;
        },
        onClick () {
            if (!this.disabled) {
                this.$emit('click');
            }
        },
        onKeydown (event) {
            if (!this.disabled) {
                this.$emit('keydown', event);
            }
        },
    },
};
</script>

<style lang="less" scoped>
.event-property-filter-tag {
    height: 24px;
    display: flex;
    padding: 4px 8px;
    align-items: center;
    cursor: pointer;
    color: var(--palette-teal-80);
    font-size: 12px;
    font-weight: 600;
    background-color: rgba(var(--raw-palette-teal-80), 0.1);

    &:hover {
        color: var(--palette-teal-90);
        background-color: rgba(var(--raw-palette-teal-70), 0.3);
    }

    &:focus-visible {
        outline: 2px solid var(--palette-gray-80);
        outline-offset: 1px;
    }

    .tag-label-icon {
        margin-right: 4px;
    }

    .tag-label-text {
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
    }

    &__disabled {
        cursor: not-allowed;
    }

    &__inapplicable {
        color: var(--palette-gray-70);
    }
}
</style>
